import React, { useMemo } from 'react';
import {
  Activity,
  BadgeCheck,
  BadgeDollarSign,
  CalendarPlus,
  CalendarX2,
  ChartPie,
  Target,
  UserPlus,
  WalletCards,
} from 'lucide-react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTheme } from '../../contexts/ThemeContext.tsx';
import type {
  DailyFunnelItem,
  FunnelBySourceItem,
  FunnelMetricsResponse,
  ProjectionsResponse,
  TimelineEvent,
} from '../../services/api.ts';
import ContactAvatar from '../ContactAvatar.tsx';
import { DASHBOARD_CHART_COLORS, getDashboardThemeClasses } from './dashboardTheme.ts';
import NPSSection from './NPSSection.tsx';

interface DashboardOverviewProps {
  stats: FunnelMetricsResponse;
  dailyFunnelData: DailyFunnelItem[];
  funnelBySource: FunnelBySourceItem[];
  timelineEvents: TimelineEvent[];
  projectionsData: ProjectionsResponse | null;
  monthlyRevenueTarget: number;
  startDate: string;
  endDate: string;
}

interface OverviewPanelProps {
  title: string;
  description?: string;
  icon?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}

const formatCurrency = (value: number): string => new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  maximumFractionDigits: 0,
}).format(Number.isFinite(value) ? value : 0);

const clampPercentage = (value: number): number => {
  if (!Number.isFinite(value)) return 0;
  return Math.min(100, Math.max(0, value));
};

const formatPeriodDate = (value: string): string => {
  const [, month, day] = value.split('-');
  return `${day}/${month}`;
};

const OverviewPanel: React.FC<OverviewPanelProps> = ({
  title,
  description,
  icon,
  className = '',
  children,
}) => {
  const { isDark } = useTheme();
  const theme = getDashboardThemeClasses(isDark);

  return (
    <article aria-label={title} className={`rounded-2xl border p-4 sm:p-5 ${theme.surface} ${className}`}>
      <header className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className={`text-sm font-semibold tracking-tight ${theme.primaryText}`}>{title}</h2>
          {description && (
            <p className={`mt-1 text-xs leading-5 ${theme.mutedText}`}>{description}</p>
          )}
        </div>
        {icon && (
          <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg ${theme.accentSurface}`}>
            {icon}
          </span>
        )}
      </header>
      {children}
    </article>
  );
};

export const DashboardContactAvatar: React.FC<{ photo?: string | null; name?: string }> = ({ photo, name }) => (
  <ContactAvatar photo={photo} name={name} testIdPrefix="activity-avatar" />
);

const getEventPresentation = (eventType: string, isDark: boolean) => {
  const normalized = eventType.toLowerCase();
  if (normalized.includes('lead')) {
    return { label: 'Novo lead recebido', Icon: UserPlus, color: isDark ? 'text-[#66A3FF]' : 'text-blue-600', bg: isDark ? 'bg-sminy-primary/15' : 'bg-blue-50' };
  }
  if (normalized.includes('agendamento')) {
    return { label: 'Lead agendou avaliação', Icon: CalendarPlus, color: isDark ? 'text-[#B8A5FF]' : 'text-violet-600', bg: isDark ? 'bg-[#9B7BFF]/15' : 'bg-violet-50' };
  }
  if (normalized.includes('comparecimento')) {
    return { label: 'Paciente compareceu', Icon: BadgeCheck, color: isDark ? 'text-[#67DBE5]' : 'text-cyan-600', bg: isDark ? 'bg-[#22C7D6]/15' : 'bg-cyan-50' };
  }
  if (normalized.includes('venda')) {
    return { label: 'Venda concluída', Icon: BadgeDollarSign, color: isDark ? 'text-[#72E3A8]' : 'text-emerald-600', bg: isDark ? 'bg-[#38C982]/15' : 'bg-emerald-50' };
  }
  if (normalized.includes('no_show')) {
    return { label: 'Falta registrada', Icon: CalendarX2, color: isDark ? 'text-rose-300' : 'text-rose-600', bg: isDark ? 'bg-rose-500/15' : 'bg-rose-50' };
  }
  return { label: 'Atualização', Icon: Activity, color: isDark ? 'text-[#A6B2C3]' : 'text-slate-600', bg: isDark ? 'bg-[#27364D]/45' : 'bg-slate-100' };
};

const getRelativeTime = (dateString: string): string => {
  const eventDate = new Date(dateString);
  if (Number.isNaN(eventDate.getTime())) return 'Data indisponível';

  const elapsedSeconds = Math.max(0, Math.floor((Date.now() - eventDate.getTime()) / 1000));
  if (elapsedSeconds < 60) return 'Agora';

  const elapsedMinutes = Math.floor(elapsedSeconds / 60);
  if (elapsedMinutes < 60) return `${elapsedMinutes} min`;

  const elapsedHours = Math.floor(elapsedMinutes / 60);
  if (elapsedHours < 24) return `${elapsedHours} h`;

  const elapsedDays = Math.floor(elapsedHours / 24);
  if (elapsedDays === 1) return 'Ontem';
  if (elapsedDays < 7) return `${elapsedDays} dias`;

  return eventDate.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
};

const DashboardOverview: React.FC<DashboardOverviewProps> = ({
  stats,
  dailyFunnelData,
  funnelBySource,
  timelineEvents,
  projectionsData,
  monthlyRevenueTarget,
  startDate,
  endDate,
}) => {
  const { isDark } = useTheme();
  const theme = getDashboardThemeClasses(isDark);

  const chartData = useMemo(() => dailyFunnelData.map((item) => {
    const [, month, day] = item.date.split('-');
    return {
      ...item,
      displayDate: `${day}/${month}`,
    };
  }), [dailyFunnelData]);

  const sortedSources = useMemo(
    () => [...funnelBySource].sort((left, right) => right.totalLeads - left.totalLeads),
    [funnelBySource],
  );
  const totalSourceLeads = sortedSources.reduce((total, source) => total + source.totalLeads, 0);
  const visibleSources = sortedSources.slice(0, 4);

  const monthlyTarget = monthlyRevenueTarget > 0
    ? monthlyRevenueTarget
    : Math.max(0, Number(projectionsData?.faturadoProjection || 0));
  const monthlyRevenue = Math.max(0, Number(projectionsData?.faturadoSoFar ?? stats.valorFaturado));
  const monthlyProgress = monthlyTarget > 0
    ? clampPercentage((monthlyRevenue / monthlyTarget) * 100)
    : 0;

  const kpis = [
    {
      label: 'Leads',
      value: stats.totalLeads,
      helper: 'Entradas no período',
      Icon: UserPlus,
      iconClass: isDark ? 'bg-sminy-primary/15 text-[#66A3FF]' : 'bg-blue-50 text-blue-600',
    },
    {
      label: 'Agendamentos',
      value: stats.totalAgendamentos,
      helper: `${stats.percentAgendamentos.toFixed(1)}% dos leads`,
      Icon: CalendarPlus,
      iconClass: isDark ? 'bg-[#9B7BFF]/15 text-[#B8A5FF]' : 'bg-violet-50 text-violet-600',
    },
    {
      label: 'Comparecimentos',
      value: stats.totalComparecimentos,
      helper: `${stats.percentComparecimentos.toFixed(1)}% dos agendamentos`,
      Icon: BadgeCheck,
      iconClass: isDark ? 'bg-[#22C7D6]/15 text-[#67DBE5]' : 'bg-cyan-50 text-cyan-600',
    },
    {
      label: 'Vendas',
      value: stats.totalVendas,
      helper: `${stats.percentVendas.toFixed(1)}% dos comparecimentos`,
      Icon: BadgeDollarSign,
      iconClass: isDark ? 'bg-[#38C982]/15 text-[#72E3A8]' : 'bg-emerald-50 text-emerald-600',
    },
  ];

  const financialItems = [
    { label: 'Orçado', value: stats.valorOrcado, valueClass: theme.primaryText },
    { label: 'Faturado', value: stats.valorFaturado, valueClass: isDark ? 'text-[#66A3FF]' : 'text-sminy-primary' },
    { label: 'Pago', value: stats.valorPago, valueClass: isDark ? 'text-[#72E3A8]' : 'text-emerald-600' },
    { label: 'Ticket médio', value: stats.ticketMedio, valueClass: theme.primaryText },
  ];

  return (
    <div data-testid="dashboard-overview" data-theme={isDark ? 'dark' : 'light'} className="space-y-5">
      <section
        data-testid="dashboard-executive-kpis"
        aria-label="Resumo do funil comercial"
        className={`grid overflow-hidden rounded-2xl border sm:grid-cols-2 xl:grid-cols-4 ${theme.surface}`}
      >
        {kpis.map(({ label, value, helper, Icon, iconClass }, index) => (
          <article
            key={label}
            className={`min-w-0 p-4 sm:p-5 ${index < 2 ? 'sm:border-b' : ''} ${index % 2 === 0 ? 'sm:border-r' : ''} ${index < 3 ? 'border-b xl:border-b-0 xl:border-r' : ''} ${theme.border}`}
          >
            <div className="flex items-center gap-3">
              <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${iconClass}`}>
                <Icon className="h-5 w-5" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <p className={`truncate text-xs font-semibold uppercase tracking-[0.08em] ${theme.mutedText}`}>{label}</p>
                <p className={`mt-0.5 text-2xl font-semibold tracking-tight ${theme.primaryText}`}>
                  {value.toLocaleString('pt-BR')}
                </p>
              </div>
            </div>
            <p className={`mt-3 truncate text-xs ${theme.mutedText}`}>{helper}</p>
          </article>
        ))}
      </section>

      <section className="grid grid-cols-1 gap-5 xl:grid-cols-12">
        <OverviewPanel
          title="Evolução do funil"
          description="Leads avançando por etapa ao longo do período"
          icon={<Activity className="h-4 w-4" aria-hidden="true" />}
          className="min-w-0 xl:col-span-8"
        >
          <div className={`mb-3 flex flex-wrap gap-x-4 gap-y-2 text-[11px] font-medium ${theme.mutedText}`}>
            {[
              ['Leads', DASHBOARD_CHART_COLORS.leads],
              ['Agendamentos', DASHBOARD_CHART_COLORS.appointments],
              ['Comparecimentos', DASHBOARD_CHART_COLORS.attendance],
              ['Vendas', DASHBOARD_CHART_COLORS.sales],
            ].map(([label, color]) => (
              <span key={label} className="inline-flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
                {label}
              </span>
            ))}
          </div>
          {chartData.length > 0 ? (
            <div className="h-[300px] w-full sm:h-[330px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 12, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid vertical={false} strokeDasharray="3 3" stroke={isDark ? '#27364D' : '#E4E8EF'} />
                  <XAxis
                    dataKey="displayDate"
                    tick={{ fontSize: 11, fill: isDark ? '#7E8CA0' : '#667085' }}
                    tickLine={false}
                    axisLine={false}
                    minTickGap={24}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 11, fill: isDark ? '#7E8CA0' : '#667085' }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <RechartsTooltip
                    labelFormatter={(label) => `Dia ${label}`}
                    contentStyle={{
                      backgroundColor: isDark ? '#0F1D31' : '#FFFFFF',
                      border: `1px solid ${isDark ? '#27364D' : '#E4E8EF'}`,
                      borderRadius: '12px',
                      color: isDark ? '#F8FAFC' : '#121B29',
                      boxShadow: '0 16px 36px -24px rgba(15, 23, 42, 0.45)',
                    }}
                  />
                  <Line type="monotone" dataKey="leads" name="Leads" stroke={DASHBOARD_CHART_COLORS.leads} strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} />
                  <Line type="monotone" dataKey="agendamentos" name="Agendamentos" stroke={DASHBOARD_CHART_COLORS.appointments} strokeWidth={2.25} dot={false} activeDot={{ r: 4 }} />
                  <Line type="monotone" dataKey="comparecimentos" name="Comparecimentos" stroke={DASHBOARD_CHART_COLORS.attendance} strokeWidth={2.25} dot={false} activeDot={{ r: 4 }} />
                  <Line type="monotone" dataKey="vendas" name="Vendas" stroke={DASHBOARD_CHART_COLORS.sales} strokeWidth={2.25} dot={false} activeDot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className={`grid h-[300px] place-items-center rounded-xl border border-dashed text-center ${theme.border}`}>
              <div>
                <Activity className="mx-auto h-7 w-7 text-slate-400" aria-hidden="true" />
                <p className={`mt-2 text-sm ${theme.mutedText}`}>Sem evolução diária neste período.</p>
              </div>
            </div>
          )}
        </OverviewPanel>

        <OverviewPanel
          title="Resultado financeiro"
          description="Visão consolidada do período"
          icon={<WalletCards className="h-4 w-4" aria-hidden="true" />}
          className="xl:col-span-4"
        >
          <dl className={`divide-y ${theme.divide}`}>
            {financialItems.map((item) => (
              <div key={item.label} className="flex items-center justify-between gap-3 py-3 first:pt-0">
                <dt className={`text-sm ${theme.mutedText}`}>{item.label}</dt>
                <dd className={`text-base font-semibold tabular-nums ${item.valueClass}`}>{formatCurrency(item.value)}</dd>
              </div>
            ))}
          </dl>

          <div className={[
            'mt-5 rounded-xl p-4',
            isDark ? 'bg-sminy-primary/10' : 'bg-sminy-blue-soft',
          ].join(' ')}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className={`text-xs font-semibold uppercase tracking-[0.08em] ${theme.accentText}`}>
                  {monthlyRevenueTarget > 0 ? 'Meta mensal' : 'Projeção mensal'}
                </p>
                <p className={`mt-1 text-lg font-semibold ${theme.primaryText}`}>
                  {monthlyTarget > 0 ? `${monthlyProgress.toFixed(0)}%` : 'Sem meta'}
                </p>
              </div>
              <span className={[
                'grid h-9 w-9 place-items-center rounded-lg',
                isDark ? 'bg-[#0F1D31] text-[#66A3FF]' : 'bg-white text-sminy-primary',
              ].join(' ')}>
                <Target className="h-4 w-4" aria-hidden="true" />
              </span>
            </div>
            <div className={`mt-3 h-2 overflow-hidden rounded-full ${isDark ? 'bg-[#27364D]/45' : 'bg-blue-100'}`}>
              <div className="h-full rounded-full bg-sminy-primary" style={{ width: `${monthlyProgress}%` }} />
            </div>
            <div className={`mt-2 flex items-center justify-between gap-3 text-[11px] ${theme.mutedText}`}>
              <span>{formatCurrency(monthlyRevenue)}</span>
              <span>{monthlyTarget > 0 ? formatCurrency(monthlyTarget) : 'Configure uma meta'}</span>
            </div>
          </div>
        </OverviewPanel>
      </section>

      <section className="grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.13fr)]">
        <OverviewPanel
          title="Leads por fonte"
          description={`Origem dos ${totalSourceLeads.toLocaleString('pt-BR')} leads no período`}
          icon={<ChartPie className="h-4 w-4" aria-hidden="true" />}
          className="h-full"
        >
          {visibleSources.length > 0 ? (
            <div className="space-y-3.5">
              {visibleSources.map((source, index) => {
                const percentage = totalSourceLeads > 0
                  ? clampPercentage((source.totalLeads / totalSourceLeads) * 100)
                  : 0;
                const barClasses = [
                  'bg-sminy-primary',
                  'bg-[#9B7BFF]',
                  'bg-[#22C7D6]',
                  'bg-[#38C982]',
                ];
                return (
                  <div key={`${source.fonte || 'Sem origem'}-${index}`}>
                    <div className="mb-1.5 flex items-center justify-between gap-3 text-xs">
                      <span className={`truncate font-medium ${theme.secondaryText}`}>{source.fonte || 'Sem origem'}</span>
                      <span className={`shrink-0 tabular-nums ${theme.mutedText}`}>
                        {source.totalLeads.toLocaleString('pt-BR')} · {percentage.toFixed(0)}%
                      </span>
                    </div>
                    <div className={`h-1.5 overflow-hidden rounded-full ${theme.track}`}>
                      <div className={`h-full rounded-full ${barClasses[index % barClasses.length]}`} style={{ width: `${percentage}%` }} />
                    </div>
                  </div>
                );
              })}
              {sortedSources.length > visibleSources.length && (
                <p className={`pt-1 text-[11px] ${theme.mutedText}`}>
                  +{sortedSources.length - visibleSources.length} outras origens no período
                </p>
              )}
            </div>
          ) : (
            <div className="grid min-h-[190px] place-items-center text-center">
              <div>
                <UserPlus className="mx-auto h-7 w-7 text-slate-400" aria-hidden="true" />
                <p className={`mt-2 text-sm ${theme.mutedText}`}>Nenhuma origem encontrada.</p>
              </div>
            </div>
          )}
        </OverviewPanel>

        <OverviewPanel
          title="Satisfação dos pacientes"
          description="Relatório de NPS consolidado no período"
          icon={<BadgeCheck className="h-4 w-4" aria-hidden="true" />}
          className="h-full"
        >
          <NPSSection startDate={startDate} endDate={endDate} variant="overview" />
        </OverviewPanel>

        <OverviewPanel
          title="Atividade recente"
          description="Últimas movimentações do funil"
          icon={<Activity className="h-4 w-4" aria-hidden="true" />}
          className="h-full lg:col-span-2 xl:col-span-1"
        >
          {timelineEvents.length > 0 ? (
            <ul className={`divide-y ${theme.divide}`}>
              {timelineEvents.slice(0, 5).map((event) => {
                const presentation = getEventPresentation(event.event_type, isDark);
                const EventIcon = presentation.Icon;
                const contactName = event.descricao || 'Contato sem nome';
                return (
                  <li key={`${event.event_type}-${event.entity_id}`} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                    <div className="relative shrink-0">
                      <DashboardContactAvatar photo={event.photo} name={contactName} />
                      <span className={`absolute -bottom-1 -right-1 grid h-5 w-5 place-items-center rounded-full border-2 ${isDark ? 'border-[#0F1D31]' : 'border-white'} ${presentation.bg} ${presentation.color}`}>
                        <EventIcon className="h-2.5 w-2.5" aria-hidden="true" />
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`truncate text-sm font-medium ${theme.primaryText}`}>{presentation.label}</p>
                      <p className={`mt-0.5 truncate text-xs ${theme.mutedText}`}>{contactName}</p>
                    </div>
                    <time className={`shrink-0 text-[11px] tabular-nums ${theme.mutedText}`} dateTime={event.event_date}>
                      {getRelativeTime(event.event_date)}
                    </time>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="grid min-h-[190px] place-items-center text-center">
              <div>
                <Activity className="mx-auto h-7 w-7 text-slate-400" aria-hidden="true" />
                <p className={`mt-2 text-sm ${theme.mutedText}`}>Nenhuma atividade recente.</p>
              </div>
            </div>
          )}
        </OverviewPanel>
      </section>

      <footer data-testid="dashboard-period-footer" className={`flex justify-end text-[11px] ${theme.mutedText}`}>
        <span>Dados consolidados · {formatPeriodDate(startDate)} a {formatPeriodDate(endDate)}</span>
      </footer>
    </div>
  );
};

export default DashboardOverview;
